import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, CreditCard, CheckCircle2, Star, Users, BookOpen, MapPin } from 'lucide-react';
import { Button } from '../ui/Button';
export function Hero() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 overflow-hidden bg-wood-dark text-cream wood-texture" data-id="element-172">
      {/* Background overlay for better text readability */}
      <div className="absolute inset-0 bg-wood-dark/80 mix-blend-multiply" data-id="element-173"></div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-174">
        <motion.div className="max-w-4xl mx-auto text-center" variants={containerVariants} initial="hidden" animate="visible" data-id="element-175">
          <motion.div variants={itemVariants} className="mb-6 inline-block" data-id="element-176">
            <span className="bg-amber/20 text-amber-light px-4 py-1.5 rounded-full text-sm font-bold tracking-wider uppercase border border-amber/30" data-id="element-177">
              Sistema N°1 en Argentina
            </span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight text-white" data-id="element-178">
            SUPER COMBO <br className="hidden md:block" data-id="element-179" />
            <span className="text-amber" data-id="element-180">CARPINTERÍA PLEGABLE PRO™</span>
          </motion.h1>

          <motion.h2 variants={itemVariants} className="text-xl md:text-3xl font-medium mb-8 text-cream-dark/90" data-id="element-181">
            Todo tu negocio de carpintería resuelto.
          </motion.h2>

          <motion.div variants={itemVariants} className="bg-wood-medium/40 border border-wood-light/30 p-6 md:p-8 rounded-2xl mb-10 backdrop-blur-sm" data-id="element-182">
            <p className="text-lg md:text-2xl italic font-serif text-amber-light" data-id="element-183">
              "Listo para fabricar. Sin buscar más planos. Sin improvisar
              medidas. Sin perder fines de semana calculando herrajes."
            </p>
            <p className="mt-4 text-sm md:text-base text-cream-dark/80" data-id="element-184">
              El sistema para carpinteros emprendedores en Argentina: 6
              proyectos completos listos (planos + herrajes + costos + ventas +
              instalación + negocio), sin buscar ni armar desde cero.
            </p>
          </motion.div>

          {/* Product Mockup */}
          <motion.div variants={itemVariants} className="mb-10" data-id="element-185">
            <motion.img src="https://cdn.magicpatterns.com/uploads/9gdXNwdVx7vFp8cA9ARTc3/heylloyd-image-1775872679322.png" alt="Super Combo Carpintería Plegable PRO - 6 Tomos completos con planos, herramientas y proyectos paso a paso" className="mx-auto max-w-md md:max-w-lg lg:max-w-xl w-full drop-shadow-2xl" animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-186" />
          </motion.div>

          <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4 mb-10 text-sm md:text-base font-medium text-cream-dark/90" data-id="element-187">
            <span className="flex items-center" data-id="element-188">
              <CheckCircle2 className="w-5 h-5 mr-2 text-amber" data-id="element-189" /> PDF
            </span>
            <span className="hidden md:inline text-wood-light" data-id="element-190">•</span>
            <span className="flex items-center" data-id="element-191">
              <CheckCircle2 className="w-5 h-5 mr-2 text-amber" data-id="element-192" /> Inmediato
            </span>
            <span className="hidden md:inline text-wood-light" data-id="element-193">•</span>
            <span className="flex items-center" data-id="element-194">
              <CheckCircle2 className="w-5 h-5 mr-2 text-amber" data-id="element-195" /> Argentina
            </span>
            <span className="hidden md:inline text-wood-light" data-id="element-196">•</span>
            <span className="flex items-center" data-id="element-197">
              <CheckCircle2 className="w-5 h-5 mr-2 text-amber" data-id="element-198" /> Todos los
              niveles
            </span>
          </motion.div>

          <motion.div variants={itemVariants} className="mb-12" data-id="element-199">
            <Button href="#comprar" size="xl" className="animate-pulse-slow w-full md:w-auto" data-id="element-200">
              QUIERO MI SUPER COMBO AHORA
            </Button>
          </motion.div>

          <motion.div variants={itemVariants} className="flex flex-col md:flex-row justify-center items-center gap-6 mb-16 text-sm text-cream-dark/80" data-id="element-201">
            <div className="flex items-center" data-id="element-202">
              <ShieldCheck className="w-5 h-5 mr-2 text-amber" data-id="element-203" />
              Garantía 7 días sin preguntas
            </div>
            <div className="flex items-center" data-id="element-204">
              <Lock className="w-5 h-5 mr-2 text-amber" data-id="element-205" />
              Pago 100% seguro
            </div>
            <div className="flex items-center" data-id="element-206">
              <CreditCard className="w-5 h-5 mr-2 text-amber" data-id="element-207" />
              Mercado Pago | Tarjeta | Transferencia
            </div>
          </motion.div>

          {/* Stats Bar */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-wood-medium/30 border border-wood-light/20 rounded-xl p-6 backdrop-blur-sm" data-id="element-208">
            <div className="flex flex-col items-center text-center" data-id="element-209">
              <Users className="w-8 h-8 mb-2 text-amber" data-id="element-210" />
              <span className="text-2xl font-bold text-white" data-id="element-211">+500</span>
              <span className="text-xs text-cream-dark/70 uppercase tracking-wider" data-id="element-212">
                Carpinteros confían
              </span>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-213">
              <BookOpen className="w-8 h-8 mb-2 text-amber" data-id="element-214" />
              <span className="text-2xl font-bold text-white" data-id="element-215">6+6</span>
              <span className="text-xs text-cream-dark/70 uppercase tracking-wider" data-id="element-216">
                Tomos + Bonos
              </span>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-217">
              <div className="flex mb-2" data-id="element-218">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 text-amber fill-amber" data-id="element-219" />)}
              </div>
              <span className="text-2xl font-bold text-white" data-id="element-220">4.9/5</span>
              <span className="text-xs text-cream-dark/70 uppercase tracking-wider" data-id="element-221">
                Calificación
              </span>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-222">
              <MapPin className="w-8 h-8 mb-2 text-amber" data-id="element-223" />
              <span className="text-2xl font-bold text-white" data-id="element-224">AR</span>
              <span className="text-xs text-cream-dark/70 uppercase tracking-wider" data-id="element-225">
                Todo el país
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}